"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CalendarRange, ChartLine as LineChart, ShieldAlert, ArrowUpRight, TrendingUp, Sparkles, Building2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const challenges = [{
  id: 1,
  title: "Corporate Weekday vs. Weekend Dip",
  subtitle: "Demand management",
  iconName: "CalendarRange",
  description: "Manage sharp drops in corporate business travel from Friday through Sunday across HITEC City and the Financial District. We implement dynamic weekend leisure packaging, staycation fencing, and event-tied rate tiers to sustain RevPAR 7 days a week.",
  metricLabel: "Strategy focus",
  metricValue: "Weekend yield fencing & staycation length-of-stay rules"
}, {
  id: 2,
  title: "MICE & Mega-Convention Compression",
  subtitle: "High-demand yield capture",
  iconName: "LineChart",
  description: "Capitalize on global summits, pharmaceutical expos, and tech summits at HITEX and HICC. Automated pace triggers and strict hurdle rates ensure your property captures peak ADR without leaving inventory stranded post-event.",
  metricLabel: "Strategy focus",
  metricValue: "Pace-activated surge pricing & strict event MLOS"
}, {
  id: 3,
  title: "Corporate Rate Parity & OTA Leakage",
  subtitle: "Channel distribution",
  iconName: "ShieldAlert",
  description: "Prevent negotiated corporate B2B rates and wholesaler inventory from leaking onto OTAs. We audit distribution flows, enforce parity across all meta-channels, and drive direct corporate portal bookings with protected margins.",
  metricLabel: "Strategy focus",
  metricValue: "B2B rate fencing & direct booking channel optimization"
}];
const seasonalTrends = [{
  id: 1,
  period: "Oct – Mar",
  title: "Peak Business, MICE & Wedding Surge",
  description: "High-density corporate travel, major HITEX/HICC conferences, and grand destination weddings. Emphasis on rate premiumization, unconstrained demand capture, and strict minimum stay controls.",
  badge: "Peak Yield"
}, {
  id: 2,
  period: "Apr – Jun",
  title: "Summer Commercial Lull",
  description: "Lower corporate project travel during annual budget cycles and extreme heat. Focus pivots to transit passengers, medical tourism, weekend staycation incentives, and high-margin banquet capture.",
  badge: "Volume Balancing"
}, {
  id: 3,
  period: "Jul – Sep",
  title: "Monsoon Tech & Pharma Steady Flow",
  description: "Gradual corporate rebound driven by biotech, pharma, and IT consulting engagements. We deploy dynamic advance purchase ladders and channel promotions to build a reliable base on high-margin terms.",
  badge: "Base Building"
}];
export default function Landscape() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.15
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="hospitalitylandscapehyderabad" data-nav="light" className="relative bg-background py-20 md:py-28 text-foreground">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="mx-auto max-w-3xl text-center mb-16 md:mb-20">
          <Badge variant="outline" className="mb-4 border-secondary/40 text-secondary-foreground">
            Market Dynamics & Yield Protection
          </Badge>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground">
            Navigating the hospitality landscape of Hyderabad
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Hyderabad is a high-velocity market shaped by tech corridors in HITEC City and Gachibowli, heavy MICE schedules, and deep weekend fluctuations. Sunil Singh engineers revenue architectures built specifically for commercial hotels, upscale boutique properties, and luxury serviced suites.
          </p>
        </div>

        {/* 3-Column Card Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {challenges.map((item, index) => <motion.div key={item.id} data-index={index} variants={cardVariants} className="h-full flex">
              <Card className="flex flex-col justify-between w-full border border-border bg-card shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-300">
                <CardHeader className="gap-3">
                  <div className="flex items-center justify-between">
                    <div className="inline-flex size-12 items-center justify-center rounded-md bg-secondary/10 text-secondary-foreground">
                      {item.iconName === "CalendarRange" && <CalendarRange className="size-6 text-chart-2" />}
                      {item.iconName === "LineChart" && <LineChart className="size-6 text-chart-2" />}
                      {item.iconName === "ShieldAlert" && <ShieldAlert className="size-6 text-chart-2" />}
                    </div>
                    <span className="text-xs font-mono font-medium text-muted-foreground tracking-wider uppercase">
                      0{index + 1}
                    </span>
                  </div>
                  <div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-secondary">
                      {item.subtitle}
                    </span>
                    <CardTitle className="font-serif text-xl sm:text-2xl font-bold mt-1.5 text-foreground leading-snug">
                      {item.title}
                    </CardTitle>
                  </div>
                </CardHeader>

                <CardContent className="space-y-6 pt-2">
                  <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                    {item.description}
                  </CardDescription>

                  <div className="rounded-md border border-border/60 bg-muted/40 p-3.5 text-xs">
                    <p className="font-semibold text-foreground mb-1">
                      {item.metricLabel}
                    </p>
                    <p className="text-muted-foreground">
                      {item.metricValue}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Hyderabad Seasonality Dynamics Block */}
        <div className="mt-16 rounded-xl border border-border bg-card p-6 sm:p-8 lg:p-10 shadow-sm">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-6 border-b border-border">
            <div>
              <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-secondary mb-1">
                <Sparkles className="size-3.5" />
                <span>Micro-Market Cycle</span>
              </div>
              <h3 className="font-serif text-2xl sm:text-3xl font-bold text-foreground">
                Hyderabad Seasonality & Demand Matrix
              </h3>
            </div>
            <p className="text-sm text-muted-foreground max-w-md">
              Proactive rate structures tailored to cyclical corporate budgets, medical travel, and MICE summits across Cyberabad.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
            {seasonalTrends.map((trend, i) => <div key={trend.id} data-index={i} className="flex flex-col justify-between rounded-lg border border-border/70 bg-background/60 p-5">
                <div>
                  <div className="flex items-center justify-between gap-2 mb-3">
                    <span className="font-mono text-xs font-bold text-secondary uppercase tracking-wider bg-secondary/10 px-2 py-0.5 rounded">
                      {trend.period}
                    </span>
                    <Badge variant="outline" className="text-[11px] border-border text-muted-foreground">
                      {trend.badge}
                    </Badge>
                  </div>
                  <h4 className="font-serif text-lg font-bold text-foreground mb-2">
                    {trend.title}
                  </h4>
                  <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                    {trend.description}
                  </p>
                </div>
              </div>)}
          </div>
        </div>

        {/* Section Bottom CTA / Footnote */}
        <div className="mt-14 md:mt-18 pt-8 border-t border-border/70 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground text-center sm:text-left">
            Need a tailored revenue assessment for your Hyderabad hotel or boutique stay?
          </p>
          <Button asChild variant="default" className="bg-primary text-primary-foreground hover:bg-primary/90">
            <Link to="#" className="inline-flex items-center gap-2">
              <span>Book a Revenue Audit</span>
              <ArrowUpRight className="size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}